import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Plus, Calendar, Check, X, AlertCircle } from "lucide-react";
import clsx from "clsx";
import {
  dayOffRequestCreateSchema,
  todayPanelTz,
  type DayOffRequest,
  type DayOffRequestCreateInput,
  type SessionUser,
} from "@axorial/shared";
import { PageHeader } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/format";

interface RequestRow {
  request: DayOffRequest;
  requesterName: string;
  requesterEmail: string;
}

export const Route = createFileRoute("/days-off")({
  component: DaysOffPage,
});

// Tipos activos en Axorial. El backend acepta también "unpaid" por compat
// con datos legacy (ver schema.dayOffRequests.kind), pero el form ya no
// lo ofrece — la política interna no usa días sin sueldo.
const KIND_LABELS: Record<DayOffRequest["kind"], string> = {
  vacation: "Vacaciones",
  sick: "Enfermedad",
  personal: "Personal",
  unpaid: "Sin sueldo",
};

const STATUS_BADGE: Record<DayOffRequest["status"], string> = {
  pending: "badge-warning",
  approved: "badge-success",
  rejected: "badge-error",
  cancelled: "badge-ghost",
};

const STATUS_LABEL: Record<DayOffRequest["status"], string> = {
  pending: "Pendiente",
  approved: "Aprobado",
  rejected: "Rechazado",
  cancelled: "Cancelado",
};

function DaysOffPage() {
  const dialogRef = useRef<HTMLDialogElement>(null);

  const me = useQuery<SessionUser>({ queryKey: ["me"], queryFn: () => api.get("/me") });
  const isApprover = me.data?.role === "admin" || me.data?.role === "lead";

  const mineQuery = useQuery<{ requests: DayOffRequest[] }>({
    queryKey: ["days-off", "me"],
    queryFn: () => api.get("/days-off/me"),
  });

  const pendingQuery = useQuery<{ requests: RequestRow[] }>({
    queryKey: ["days-off", "pending"],
    queryFn: () => api.get("/days-off/pending"),
    enabled: isApprover,
  });

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="días libres"
        title="Vacaciones, sick, personal"
        subtitle="Solicita días libres, gestiona tus pendientes y, si eres lead/admin, aprueba o rechaza solicitudes del equipo."
        actions={
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => dialogRef.current?.showModal()}
          >
            <Plus className="size-4" /> Solicitar días
          </button>
        }
      />

      {isApprover && (
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-base-content/70">
              Pendientes de aprobación
            </h3>
            <span className="text-xs text-base-content/55">
              {pendingQuery.data?.requests.length ?? 0} pendientes
            </span>
          </div>
          {pendingQuery.data && pendingQuery.data.requests.length === 0 && (
            <p className="text-sm text-base-content/55 italic">Sin solicitudes pendientes.</p>
          )}
          <ul className="space-y-2">
            {pendingQuery.data?.requests
              .filter((r) => r.request.userId !== me.data?.id) // no aprobar propias
              .map((row) => (
                <PendingRow key={row.request.id} row={row} />
              ))}
          </ul>
        </section>
      )}

      <section className="space-y-3">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-base-content/70">
          Mis solicitudes
        </h3>
        {mineQuery.data && mineQuery.data.requests.length === 0 ? (
          <div className="card-elevated py-12 text-center">
            <div className="size-12 rounded-full bg-primary/10 grid place-items-center mx-auto mb-3">
              <Calendar className="size-5 text-primary" />
            </div>
            <p className="text-sm text-base-content/65 mb-3">
              No has solicitado días libres todavía.
            </p>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => dialogRef.current?.showModal()}
            >
              Solicitar
            </button>
          </div>
        ) : (
          <ul className="space-y-2">
            {mineQuery.data?.requests.map((r) => (
              <MyRow key={r.id} request={r} />
            ))}
          </ul>
        )}
      </section>

      <CreateDialog ref={dialogRef} onClose={() => dialogRef.current?.close()} />
    </div>
  );
}

// ─── Rows ─────────────────────────────────────────────────────────────────

function MyRow({ request }: { request: DayOffRequest }) {
  const qc = useQueryClient();
  const cancel = useMutation({
    mutationFn: () => api.del(`/days-off/${request.id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["days-off"] }),
  });
  const range =
    request.startDate === request.endDate
      ? formatDate(request.startDate)
      : `${formatDate(request.startDate)} → ${formatDate(request.endDate)}`;
  return (
    <li className="card-elevated p-4 flex items-center gap-3">
      <Calendar className="size-4 text-base-content/40 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium">{range}</div>
        <div className="text-xs text-base-content/55 mt-0.5">
          {KIND_LABELS[request.kind]}
          {request.reason && ` · ${request.reason}`}
        </div>
        {request.approverComment && (
          <div className="text-xs italic text-base-content/65 mt-1.5">
            “{request.approverComment}”
          </div>
        )}
      </div>
      <span className={`badge badge-sm ${STATUS_BADGE[request.status]}`}>
        {STATUS_LABEL[request.status]}
      </span>
      {request.status === "pending" && (
        <button
          type="button"
          onClick={() => cancel.mutate()}
          disabled={cancel.isPending}
          className="btn btn-ghost btn-xs"
        >
          Cancelar
        </button>
      )}
    </li>
  );
}

function PendingRow({ row }: { row: RequestRow }) {
  const qc = useQueryClient();
  const decide = useMutation({
    mutationFn: (decision: { status: "approved" | "rejected"; approverComment?: string }) =>
      api.patch(`/days-off/${row.request.id}/decision`, decision),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["days-off"] }),
  });
  const range =
    row.request.startDate === row.request.endDate
      ? formatDate(row.request.startDate)
      : `${formatDate(row.request.startDate)} → ${formatDate(row.request.endDate)}`;
  return (
    <li className="card-elevated p-4 flex flex-col sm:flex-row sm:items-center gap-3 border-l-4 border-l-warning">
      <div className="size-9 rounded-full bg-primary/10 grid place-items-center text-primary font-semibold uppercase shrink-0">
        {row.requesterName
          .split(" ")
          .slice(0, 2)
          .map((p) => p[0])
          .join("")}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium">{row.requesterName}</div>
        <div className="text-xs text-base-content/55">
          {range} · {KIND_LABELS[row.request.kind]}
          {row.request.reason && ` · ${row.request.reason}`}
        </div>
      </div>
      <div className="flex gap-1.5">
        <button
          type="button"
          className="btn btn-success btn-sm"
          onClick={() => decide.mutate({ status: "approved" })}
          disabled={decide.isPending}
        >
          <Check className="size-3.5" /> Aprobar
        </button>
        <button
          type="button"
          className="btn btn-error btn-outline btn-sm"
          onClick={() => {
            const comment = prompt("Razón del rechazo (opcional):");
            decide.mutate({
              status: "rejected",
              approverComment: comment ?? undefined,
            });
          }}
          disabled={decide.isPending}
        >
          <X className="size-3.5" /> Rechazar
        </button>
      </div>
    </li>
  );
}

// ─── Create dialog ────────────────────────────────────────────────────────

import { forwardRef } from "react";

const CreateDialog = forwardRef<HTMLDialogElement, { onClose: () => void }>(function CreateDialog(
  { onClose },
  ref,
) {
  const qc = useQueryClient();
  const today = todayPanelTz();
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<DayOffRequestCreateInput>({
    resolver: zodResolver(dayOffRequestCreateSchema),
    defaultValues: { kind: "vacation", startDate: today, endDate: today },
  });

  const create = useMutation({
    mutationFn: (input: DayOffRequestCreateInput) => api.post("/days-off", input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["days-off"] });
      reset();
      onClose();
    },
  });

  const start = watch("startDate");

  return (
    <dialog ref={ref} className="modal" onClose={() => reset()}>
      <div className="modal-box card-elevated">
        <h3 className="text-lg font-display font-semibold mb-4">Solicitar días libres</h3>
        <form onSubmit={handleSubmit((d) => create.mutate(d))} className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label py-1">
                <span className="label-text text-sm font-medium">Inicio</span>
              </label>
              <input
                type="date"
                className="input input-bordered w-full"
                min={today}
                {...register("startDate")}
              />
              {errors.startDate && (
                <span className="text-xs text-error mt-1 block">{errors.startDate.message}</span>
              )}
            </div>
            <div>
              <label className="label py-1">
                <span className="label-text text-sm font-medium">Fin</span>
              </label>
              <input
                type="date"
                className="input input-bordered w-full"
                min={start}
                {...register("endDate")}
              />
              {errors.endDate && (
                <span className="text-xs text-error mt-1 block">{errors.endDate.message}</span>
              )}
            </div>
          </div>

          <div>
            <label className="label py-1">
              <span className="label-text text-sm font-medium">Tipo</span>
            </label>
            <div className="flex flex-wrap gap-1.5">
              {(["vacation", "sick", "personal"] as const).map((k) => (
                <KindPill key={k} value={k} register={register} />
              ))}
            </div>
          </div>

          <div>
            <label className="label py-1">
              <span className="label-text text-sm font-medium">Razón (opcional)</span>
            </label>
            <textarea
              rows={2}
              className="textarea textarea-bordered w-full"
              placeholder="Para tu lead/admin, contexto opcional"
              {...register("reason")}
            />
          </div>

          <div className="rounded-md bg-base-200/60 px-3 py-2 text-xs text-base-content/65 flex gap-2">
            <AlertCircle className="size-3.5 mt-0.5 shrink-0" />
            <span>
              Tu solicitud queda en pendiente hasta que un lead o admin la apruebe. Te notificarán
              por Mattermost cuando haya decisión.
            </span>
          </div>

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2 border-t border-base-300/60">
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => {
                reset();
                onClose();
              }}
            >
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary btn-sm" disabled={create.isPending}>
              {create.isPending && <span className="loading loading-spinner loading-xs" />}
              Solicitar
            </button>
          </div>
        </form>
      </div>
      <form method="dialog" className="modal-backdrop">
        <button>cerrar</button>
      </form>
    </dialog>
  );
});

function KindPill({
  value,
  register,
}: {
  value: DayOffRequest["kind"];
  register: ReturnType<typeof useForm<DayOffRequestCreateInput>>["register"];
}) {
  return (
    <label
      className={clsx(
        "badge badge-lg cursor-pointer has-[:checked]:badge-primary",
        "transition-colors",
      )}
    >
      <input type="radio" value={value} className="hidden" {...register("kind")} />
      {KIND_LABELS[value]}
    </label>
  );
}
